import { usersApi } from "@/modules/users/presentation/api/client";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { Button, DatePicker } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { format, subDays } from "date-fns";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";

type UserItem = { id: string; name?: string; email?: string };

export type RevenueSalesReportSearch = {
  dateFrom?: string;
  dateTo?: string;
  issuedById?: string;
  groupBy?: "day" | "month";
};

export function RevenueSalesReportFilter() {
  const nav = useNavigate({ from: "/app/reports/revenue" });
  const search = useSearch({
    from: "/app/reports/revenue",
  }) as RevenueSalesReportSearch;

  const defaultFrom = format(subDays(new Date(), 30), "yyyy-MM-dd");
  const defaultTo = format(new Date(), "yyyy-MM-dd");

  const [fromDate, setFromDate] = useState(search.dateFrom ?? defaultFrom);
  const [toDate, setToDate] = useState(search.dateTo ?? defaultTo);
  const [issuedById, setIssuedById] = useState(search.issuedById ?? "");
  const [groupBy, setGroupBy] = useState<"day" | "month">(
    search.groupBy ?? "day",
  );

  useEffect(() => {
    setFromDate(search.dateFrom ?? defaultFrom);
    setToDate(search.dateTo ?? defaultTo);
    setIssuedById(search.issuedById ?? "");
    setGroupBy(search.groupBy ?? "day");
  }, [search.dateFrom, search.dateTo, search.issuedById, search.groupBy]);

  const hasFilter = issuedById || search.dateFrom || search.dateTo;

  const applyFilters = (updates: Partial<RevenueSalesReportSearch>) => {
    const next = {
      dateFrom: updates.dateFrom ?? fromDate,
      dateTo: updates.dateTo ?? toDate,
      issuedById:
        updates.issuedById !== undefined
          ? updates.issuedById
          : issuedById
            ? issuedById
            : undefined,
      groupBy: updates.groupBy ?? groupBy,
    };
    nav({ search: next });
  };

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <DatePicker
            mode="range"
            value={{
              from: fromDate ? new Date(fromDate) : undefined,
              to: toDate ? new Date(toDate) : undefined,
            }}
            onChange={(range) => {
              const from: Date | undefined = Array.isArray(range)
                ? range[0]
                : (range as { from?: Date; to?: Date })?.from;
              const to: Date | undefined = Array.isArray(range)
                ? range[1]
                : (range as { from?: Date; to?: Date })?.to;
              const fromStr = from ? format(from, "yyyy-MM-dd") : defaultFrom;
              const toStr = to ? format(to, "yyyy-MM-dd") : defaultTo;
              setFromDate(fromStr);
              setToDate(toStr);
              applyFilters({ dateFrom: fromStr, dateTo: toStr });
            }}
            placeholder="ຊ່ວງວັນທີ"
            className="h-8 w-full sm:w-fit"
          />

          <InfiniteCombobox<UserItem>
            queryKey={["users", "revenue-report"]}
            queryFn={async ({ search: q, pageParam }) => {
              const result = await usersApi.list({
                limit: 20,
                offset: (pageParam - 1) * 20,
                filters: q
                  ? [{ field: "name", op: "contains", value: q }]
                  : undefined,
              });
              return {
                items: result.data,
                nextPage:
                  result.data.length === 20 &&
                  result.meta.total > pageParam * 20
                    ? pageParam + 1
                    : null,
              };
            }}
            preloadQueryFn={async (id) => {
              const result = await usersApi.get(id);
              return result ?? undefined;
            }}
            getLabel={(item) => item.name || item.email || item.id}
            getValue={(item) => item.id}
            value={issuedById}
            onValueChange={(val) => {
              setIssuedById(val);
              applyFilters({ issuedById: val || undefined });
            }}
            placeholder="ອອກໂດຍ (ທັງໝົດ)"
            className="h-8 w-full sm:w-48"
            clearable
          />

          <SimpleSelect
            placeholder="ຕາຕະລາງແບ່ງຕາມ"
            options={[
              { label: "ຕໍ່ວັນ", value: "day" },
              { label: "ຕໍ່ເດືອນ", value: "month" },
            ]}
            value={groupBy}
            onValueChange={(val) => {
              setGroupBy(val as "day" | "month");
              applyFilters({ groupBy: val as "day" | "month" });
            }}
            className="h-8 w-full sm:w-48"
          />
        </div>

        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setIssuedById("");
              setFromDate(defaultFrom);
              setToDate(defaultTo);
              setGroupBy("day");
              nav({
                search: {
                  dateFrom: defaultFrom,
                  dateTo: defaultTo,
                  groupBy: "day",
                },
              });
            }}
            className="max-sm:w-full"
          >
            <XIcon className="size-4" />
            ລ້າງ
          </Button>
        )}
      </div>
    </div>
  );
}
